<template>
  <FormSection
    id="form-general-recurring-settings"
    v-if="canSetRecurring && featureEnabled && isAuthorized"
  >
    <Row
      type="flex"
      align="baseline"
      justify="space-between"
      class="recurring-header"
    >
      <Column :md="10">
        <FormGroup
          class="recurring_enabled"
          prop="recurring_enabled"
          :no-margin="true"
        >
          <ToggleSwitch
            class="state-switch no-wrap"
            v-model="recurringEnabled"
            data-test="recurring_enabled"
            :disabled="recurring_draft || !userHasPremiumPlan"
            id="recurring_enabled"
            @click.native.stop="showUpgradeDialog"
          >
            <template slot="active-content">
              <div class="recurring-label">
                {{ $t('form.invoiceForm.fields.recurring.enabled.label') }}
                <Tooltip
                  effect="blue"
                  placement="right"
                  :open-delay="100"
                  :content="$t(`form.invoiceForm.fields.recurring.inputHelp`)"
                >
                  <i
                    class="icon icon-question-circle"
                    style="margin-left: 5px; cursor: help;"
                  />
                </Tooltip>
              </div>
            </template>
          </ToggleSwitch>
        </FormGroup>
      </Column>
      <Column :md="14">
        <div
          v-if="recurringEnabled"
          class="recurring-dates"
        >
          <div
            data-test="recurring_current_invoice_date"
            class="recurring-date"
          >
            <span class="text--md text--secondary-color">
              {{ $t('form.invoiceForm.fields.recurring.currentInvoiceDate') }}
            </span>
            <span class="text--lg text--semibold">{{ issued_on | formatDate('dd. MM. yyyy') }}</span>
            <Anchor
              @click="handleChangeDateAnchorClick"
              ref="anchor"
            >
              {{ $t('form.invoiceForm.fields.recurring.changeCurrentInvoiceDate') }}
            </Anchor>
            <DatePicker
              v-model="issued_on"
              ref="datepicker"
              value-format="timestamp"
              :append-to-body="true"
              :format="dateFormats.elementUi"
              class="datepicker-helper"
              @change="handleDateSelected"
            />
          </div>
          <div
            data-test="recurring_next_invoice_date"
            class="recurring-date"
          >
            <span class="text--md text--secondary-color">
              {{ $t('form.invoiceForm.fields.recurring.nextInvoiceDate') }}
            </span>
            <span class="text--lg text--semibold">{{ nextInvoiceDate | formatDate('dd. MM. yyyy') }}</span>
          </div>
        </div>
      </Column>
    </Row>

    <Row
      type="flex"
      align="top"
      :gutter="16"
      v-if="recurringEnabled"
      class="recurring-settings"
    >
      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          prop="recurring_frequency"
          :label="$t('form.invoiceForm.fields.recurring.frequency.label')"
          vertical
          floating-label
          has-input-value
        >
          <Select
            class="select-v2"
            name="language"
            v-model="recurring_frequency"
            autocomplete="new-password"
            data-test="recurring_frequency"
            filterable
            :popper-append-to-body="false"
          >
            <Option
              v-for="option in frequencyOptions"
              :key="option.key"
              :label="option.value"
              :value="option.key"
            />
          </Select>
        </FormGroup>
      </Column>
      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          prop="recurring_end_options"
          :label="$t('form.invoiceForm.fields.recurring.endOptions.label')"
          vertical
          floating-label
          has-input-value
        >
          <Select
            class="select-v2"
            name="language"
            v-model="recurring_end_options"
            autocomplete="new-password"
            data-test="recurring_end_options"
            filterable
            :popper-append-to-body="false"
          >
            <Option
              v-for="option in whenEndOptions"
              :key="option.key"
              :label="option.value"
              :value="option.key"
            />
          </Select>
        </FormGroup>
      </Column>

      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          vertical
          prop="recurring_ends_on"
          :label="$t('form.invoiceForm.fields.recurring.endAfterDate.label')"
          v-if="showDateInput"
          floating-label
          has-input-value
        >
          <DatePicker
            v-model="recurring_ends_on"
            data-test="recurring_ends_on"
            :clearable="false"
            :format="dateFormats.elementUi"
            :picker-options="pickerOptions"
            :default-value="firstAvailableDate"
          />
        </FormGroup>
        <FormGroup
          vertical
          prop="recurring_ends_after_count"
          :label="$t('form.invoiceForm.fields.recurring.endAfterCount.label')"
          v-if="showCountInput"
          floating-label
          has-input-value
        >
          <TextInput
            name="recurring_ends_after_count"
            data-test="recurring_ends_after_count"
            type="number"
            v-model.number="recurring_ends_after_count"
            id="recurring_ends_after_count"
          />
        </FormGroup>
      </Column>
    </Row>

    <Row
      type="flex"
      align="top"
      class="recurring-checkboxes"
      :gutter="16"
      line
      v-if="recurringEnabled"
    >
      <Column
        :xs="24"
        :sm="12"
        :md="8"
        v-if="canSetRecurringLastDayInMonth"
      >
        <FormGroup
          class="iban-form-group recurring-checkbox"
          prop="recurring_issue_last_day"
        >
          <Checkbox
            v-model="recurring_issue_last_day"
            data-test="recurring_issue_last_day"
            id="recurring_issue_last_day"
          >
            {{ $t('form.invoiceForm.fields.recurring.issueLastDay.label') }}
          </Checkbox>
        </FormGroup>
      </Column>
      <Column
        :xs="24"
        :sm="12"
        :md="8"
      >
        <FormGroup
          class="iban-form-group recurring-checkbox"
          prop="recurring_auto_submit"
        >
          <Checkbox
            v-model="recurring_auto_submit"
            data-test="recurring_auto_submit"
            id="recurring_auto_submit"
          >
            {{ $t('form.invoiceForm.fields.recurring.autoSubmit.label') }}
          </Checkbox>
        </FormGroup>
      </Column>

      <Column
        :xs="24"
        :sm="12"
        :md="8"
        v-if="canSetCopyTomyEmail"
      >
        <FormGroup
          class="iban-form-group recurring-checkbox"
          prop="recurring_submit_copy"
        >
          <Checkbox
            v-model="recurring_submit_copy"
            data-test="recurring_submit_copy"
            id="recurring_submit_copy"
          >
            {{ $t('form.invoiceForm.fields.recurring.submitCopy.label') }}
          </Checkbox>
        </FormGroup>
      </Column>
    </Row>
  </FormSection>
</template>

<script>
import RecurringSettings from '../../../InvoiceForm/sections/RecurringSettings.vue';

export default {
  extends: RecurringSettings,
};
</script>

<style lang="scss" scoped>
.recurring-settings {
  margin-top: 10px;
}

.datepicker-helper {
  display: none;
}

.recurring-header {
  margin-bottom: 32px;
  row-gap: 1rem;
}

.recurring-dates {
  display: flex;
  justify-content: flex-end;
  gap: 1rem 2rem;
  flex-wrap: nowrap;

  @media only screen and (max-width: ($--md - 1px)) {
    justify-content: flex-start;
    flex-wrap: wrap;
  }
}

.recurring-date {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.recurring-label {
  font-size: rem(13px);
  position: relative;
  top: -4px;
  left: -3px;

  .icon {
    color: $--color-primary;
    font-size: rem(18px);
    position: relative;
    top: 4px;
  }
}

.recurring-checkboxes {
  @media only screen and (max-width: ($--md - 1px)) {
    margin-top: 1rem;
  }
}

.recurring-checkbox {
  padding-left: 12px;
  padding-right: 8px;
}
</style>
